import React from 'react';
import { Calendar, CheckCircle, RefreshCw, AlertCircle, Loader } from 'lucide-react';

interface GoogleCalendarConnectProps {
  isConnected: boolean;
  googleCalendarId: string | null;
  isSyncing: boolean;
  isConnecting: boolean;
  syncResult: { synced: number; failed: number } | null;
  onConnect: () => void;
  onReconnect: () => void;
}

export default function GoogleCalendarConnect({
  isConnected,
  googleCalendarId,
  isSyncing,
  isConnecting,
  syncResult,
  onConnect,
  onReconnect,
}: GoogleCalendarConnectProps) {
  if (isConnected && googleCalendarId) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-green-800">Google Calendar connected</p>
              <p className="text-xs text-green-600 mt-0.5 font-mono truncate max-w-xs">
                {googleCalendarId}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onReconnect}
            disabled={isConnecting || isSyncing}
            className="ml-4 shrink-0 text-sm text-green-700 hover:text-green-900 underline disabled:opacity-50"
          >
            Reconnect
          </button>
        </div>

        {isSyncing && (
          <div className="mt-3 flex items-center gap-2 text-sm text-green-700">
            <Loader className="h-4 w-4 animate-spin" />
            Syncing events to Google Calendar...
          </div>
        )}

        {syncResult && !isSyncing && (
          <div className="mt-3 text-sm">
            {syncResult.synced > 0 && (
              <p className="text-green-700">
                {syncResult.synced} event{syncResult.synced !== 1 ? 's' : ''} synced successfully
              </p>
            )}
            {syncResult.failed > 0 && (
              <p className="text-red-600 flex items-center gap-1 mt-1">
                <AlertCircle className="h-3.5 w-3.5" />
                {syncResult.failed} event{syncResult.failed !== 1 ? 's' : ''} failed to sync
              </p>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="flex items-center gap-3 mb-3">
        <Calendar className="h-5 w-5 text-gray-500" />
        <div>
          <p className="text-sm font-medium text-gray-700">Google Calendar</p>
          <p className="text-xs text-gray-500">
            Connect Google Calendar to sync your events automatically via OAuth
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={onConnect}
        disabled={isConnecting || isSyncing}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
      >
        {isConnecting ? (
          <>
            <RefreshCw className="h-4 w-4 animate-spin" />
            Connecting...
          </>
        ) : (
          <>
            <Calendar className="h-4 w-4" />
            Connect Google Calendar
          </>
        )}
      </button>
    </div>
  );
}
